import { IProductWarranties, IWarrantyOption } from '../../actions/DataServiceEntities.g';
import { CommerceProperty } from '@msdyn365-commerce/retail-proxy';
import { IGroupedItemLine, ILineItem, ProductLineCommerceProperties, ProductLineDataType } from '../utils';

/**
 * Searches the provided list of cartlines for the provided item to determine what its
 * currently selected warranty is.
 * @param recordId Id of the product which
 * @param pairedCartLine The warranty in this item/warranty pair contains the list on which the
 * selected item is searched.
 * @param productWarrantiesForCartline The list of active product warranties in the cart. Most modules that
 * concern the cart have this in their data actions.
 */
export function findSelectedWarranty<T extends ILineItem>(
    recordId: number,
    pairedItemLine: IGroupedItemLine<T>,
    productWarrantiesForItemLine: IProductWarranties | undefined,
    lineDataType: ProductLineDataType
): IWarrantyOption | undefined {
    if (
        productWarrantiesForItemLine &&
        productWarrantiesForItemLine.WarrantyGroups &&
        productWarrantiesForItemLine?.WarrantyGroups[0].WarrantyOptions &&
        productWarrantiesForItemLine?.WarrantyGroups[0].WarrantyOptions.length > 0
    ) {
        const selectedWarranty = productWarrantiesForItemLine?.WarrantyGroups[0].WarrantyOptions.find(
            warrantyOption =>
                warrantyOption.Period ===
                pairedItemLine.warranty?.data[`${lineDataType}`].ExtensionProperties?.find(
                    (commerceProp: CommerceProperty) => commerceProp.Key === ProductLineCommerceProperties.warrantyPeriod
                )?.Value?.IntegerValue
        );
        if (selectedWarranty) {
            return selectedWarranty;
        }
        return getEmptyWarrantyOption(recordId ?? 0, productWarrantiesForItemLine?.WarrantyGroups[0].WarrantyOptions);
    }
    return undefined;
}

/**
 * Returns a full warranty option from the provided list based on the provided term name.
 *
 * @param warrantyOptions A list of warranty options for a product.
 * @param period The duration the warranty is valid for, in string or number format. (note that warranty options have no unique id)
 */
export function getWarrantyOptionByPeriod(warrantyOptions: IWarrantyOption[], period: string | number): IWarrantyOption | undefined {
    if (typeof period === 'string') {
        period = parseInt(period, 10);
    }
    if (period === 0) {
        return getEmptyWarrantyOption(0, warrantyOptions);
    } else {
        return getWarrantyOptionsInternal(warrantyOptions, period);
    }
}

/*
 * Internal function. Returns a full warranty option from the provided list based on the provided term name.
 *
 * @param warrantyOptions - A list of warranty options for a product.
 * @param period - The duration the warranty is valid for. (note that warranty options have no unique id)
 */
function getWarrantyOptionsInternal(warrantyOptions: IWarrantyOption[], period: number): IWarrantyOption | undefined {
    return warrantyOptions.find(option => {
        return option.Period === period;
    });
}

/**
 * Returns a warranty with a period of 0. This function can be called without warranty options, but this is not recommended.
 *
 * @param warrantyOptions A list of warranty options for a product.
 * @param period The duration the warranty is valid for, in string or number format. (note that warranty options have no unique id)
 * @param warrantyType The associated warrranty type (e.g. EW/IPR).
 */
export function getEmptyWarrantyOption(
    warrantableProduct: number,
    warrantyOptions?: IWarrantyOption[],
    warrantyType?: string
): IWarrantyOption | undefined {
    let emptyWarranty: IWarrantyOption | undefined;
    if (warrantyOptions) {
        emptyWarranty = getWarrantyOptionsInternal(warrantyOptions, 0);
    }
    if (!emptyWarranty) {
        const emptyWarrantyType = warrantyType ?? (warrantyOptions ? warrantyOptions[0].Type : undefined);
        emptyWarranty = {
            Charge: 0,
            Description: 'None',
            Period: 0,
            Product: 0,
            Rate: 0,
            RecordId: 1,
            TermName: `${emptyWarrantyType} None`,
            Type: emptyWarrantyType,
            WarrantableProduct: +warrantableProduct,
            WarrantableProductPrice: 0
        };
    }
    return emptyWarranty;
}
